import * as React from 'react'
import { createContext, useContext } from 'react'

import { Slot } from '@radix-ui/react-slot'

import { Icon } from '@/shared/components/icon'
import { cn } from '@/shared/lib'

type StepperContextValue = {
  activeStep: number
  setActiveStep: (step: number) => void
  orientation: 'horizontal' | 'vertical'
}

type StepItemContextValue = {
  step: number
  state: StepState
  isDisabled: boolean
  isLoading: boolean
}

const STEP_STATE = {
  COMPLETED: 'completed',
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const
type StepState = (typeof STEP_STATE)[keyof typeof STEP_STATE]

const StepperContext = createContext<StepperContextValue | undefined>(undefined)
const StepItemContext = createContext<StepItemContextValue | undefined>(undefined)

const useStepper = () => {
  const context = useContext(StepperContext)
  if (!context) {
    throw new Error('useStepper must be used within a Stepper')
  }
  return context
}

const useStepItem = () => {
  const context = useContext(StepItemContext)
  if (!context) {
    throw new Error('useStepItem must be used within a StepperItem')
  }
  return context
}
interface StepperProps extends React.HTMLAttributes<HTMLDivElement> {
  defaultValue?: number
  value?: number
  onValueChange?: (value: number) => void
  orientation?: 'horizontal' | 'vertical'
}

function Stepper({
  defaultValue = 0,
  value,
  onValueChange,
  orientation = 'horizontal',
  className,
  ...props
}: Readonly<StepperProps>) {
  const [activeStep, setInternalStep] = React.useState(defaultValue)

  const setActiveStep = React.useCallback(
    (step: number) => {
      if (value === undefined) {
        setInternalStep(step)
      }
      onValueChange?.(step)
    },
    [value, onValueChange],
  )

  const currentStep = value ?? activeStep
  const values = React.useMemo(
    () => ({
      activeStep: currentStep,
      setActiveStep,
      orientation,
    }),
    [currentStep, setActiveStep, orientation],
  )

  return (
    <StepperContext.Provider value={values}>
      <div
        data-slot="stepper"
        className={cn(
          'group/stepper inline-flex data-[orientation=horizontal]:w-full data-[orientation=horizontal]:flex-row data-[orientation=vertical]:flex-col',
          className,
        )}
        data-orientation={orientation}
        {...props}
      />
    </StepperContext.Provider>
  )
}
interface StepperItemProps extends React.HTMLAttributes<HTMLDivElement> {
  step: number
  completed?: boolean
  disabled?: boolean
  loading?: boolean
}

function StepperItem({
  step,
  completed = false,
  disabled = false,
  loading = false,
  className,
  children,
  ...props
}: Readonly<StepperItemProps>) {
  const { activeStep } = useStepper()

  const activeState = activeStep === step ? STEP_STATE.ACTIVE : STEP_STATE.INACTIVE
  const state: StepState = completed || step < activeStep ? STEP_STATE.COMPLETED : activeState

  const isLoading = loading && step === activeStep

  const values = React.useMemo(
    () => ({
      step,
      state,
      isDisabled: disabled,
      isLoading,
    }),
    [step, state, disabled, isLoading],
  )

  return (
    <StepItemContext.Provider value={values}>
      <div
        data-slot="stepper-item"
        className={cn(
          'group/step flex items-center group-data-[orientation=horizontal]/stepper:flex-row group-data-[orientation=vertical]/stepper:flex-col',
          className,
        )}
        data-state={state}
        {...(isLoading ? { 'data-loading': true } : {})}
        {...props}
      >
        {children}
      </div>
    </StepItemContext.Provider>
  )
}

interface StepperTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
  onIconClick?: (func: () => void) => void
}

function StepperTrigger({
  asChild = false,
  className,
  children,
  onIconClick,
  ...props
}: Readonly<StepperTriggerProps>) {
  const { setActiveStep } = useStepper()
  const { step, isDisabled } = useStepItem()

  if (asChild) {
    const Comp = asChild ? Slot : 'span'
    return (
      <Comp data-slot="stepper-trigger" className={className}>
        {children}
      </Comp>
    )
  }

  const handleActiveStepChange = () => {
    if (isDisabled) return
    setActiveStep(step)
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (onIconClick) {
      onIconClick(handleActiveStepChange)
      return
    }
    handleActiveStepChange()
  }

  return (
    <button
      data-slot="stepper-trigger"
      className={cn(
        'focus-visible:border-ring focus-visible:ring-ring/50 inline-flex items-center gap-3 rounded-full outline-none focus-visible:z-10 focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50',
        className,
      )}
      onClick={handleClick}
      disabled={isDisabled}
      {...props}
    >
      {children}
    </button>
  )
}

interface StepperIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean
  iconName?: React.ComponentProps<typeof Icon>['name']
}

function StepperIndicator({
  asChild = false,
  className,
  children,
  iconName = 'check',
  ...props
}: Readonly<StepperIndicatorProps>) {
  const { state, step } = useStepItem()
  return (
    <span
      data-slot="stepper-indicator"
      className={cn(
        'bg-muted text-muted-foreground data-[state=active]:bg-primary data-[state=completed]:bg-primary data-[state=active]:text-primary-foreground data-[state=completed]:text-primary-foreground relative flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-medium',
        className,
      )}
      data-state={state}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          <span className="transition-all group-data-loading/step:scale-0 group-data-loading/step:opacity-0 group-data-loading/step:transition-none group-data-[state=completed]/step:scale-0 group-data-[state=completed]/step:opacity-0">
            {step + 1}
          </span>
          <Icon
            className="absolute h-4 w-4 scale-0 opacity-0 transition-all group-data-[state=completed]/step:scale-100 group-data-[state=completed]/step:opacity-100"
            name={iconName}
          />
        </>
      )}
    </span>
  )
}

function StepperSeparator({ className, ...props }: Readonly<React.HTMLAttributes<HTMLDivElement>>) {
  return (
    <div
      data-slot="stepper-separator"
      className={cn(
        'bg-muted group-data-[state=completed]/step:bg-primary m-0.5 group-data-[orientation=horizontal]/stepper:h-0.5 group-data-[orientation=horizontal]/stepper:w-full group-data-[orientation=horizontal]/stepper:flex-1 group-data-[orientation=vertical]/stepper:h-12 group-data-[orientation=vertical]/stepper:w-0.5',
        className,
      )}
      {...props}
    />
  )
}

export { Stepper, StepperIndicator, StepperItem, StepperSeparator, StepperTrigger }
